"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import { mediaQuery } from "@/lib/media-query";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { type Resolver, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useMediaQuery } from "usehooks-ts";
import { z } from "zod/v4";

export type ReportDialogTarget =
  | { type: "LISTING"; listingId: number }
  | { type: "BROKER"; userId: string }
  | { type: "CONVERSATION"; conversationId: number };

export type ReportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ReportDialogTarget;
};

function buildFormSchema(reasons: readonly string[]) {
  const allowed = new Set(reasons);
  return z
    .object({
      reason: z.string(),
      details: z
        .string()
        .max(1000)
        .optional()
        .transform((v) => (v?.trim() === "" ? undefined : v)),
    })
    .superRefine((data, ctx) => {
      if (!allowed.has(data.reason)) {
        ctx.addIssue({
          code: "custom",
          message: "invalid",
          path: ["reason"],
        });
      }
      if (data.reason === "OTHER" && !data.details?.length) {
        ctx.addIssue({
          code: "custom",
          message: "required",
          path: ["details"],
        });
      }
    });
}

type ReportFormInnerProps = {
  reasons: string[];
  target: ReportDialogTarget;
  isDesktop: boolean;
  onOpenChange: (open: boolean) => void;
};

function ReportFormInner({
  reasons,
  target,
  isDesktop,
  onOpenChange,
}: ReportFormInnerProps) {
  const t = useTranslations();
  const { displayErrorToast, formatError } = useErrorFormatter();

  const formSchema = buildFormSchema(reasons);

  type ReportFormValues = {
    reason: string;
    details?: string;
  };

  const form = useForm<ReportFormValues>({
    resolver: standardSchemaResolver(
      formSchema,
    ) as Resolver<ReportFormValues>,
    defaultValues: {
      reason: reasons[0] ?? "",
      details: "",
    },
  });

  useEffect(() => {
    form.reset({
      reason: reasons[0] ?? "",
      details: "",
    });
  }, [reasons, form]);

  const createMutation = api.reports.create.useMutation();

  const onSubmit: SubmitHandler<ReportFormValues> = async (values) => {
    try {
      if (target.type === "LISTING") {
        await createMutation.mutateAsync({
          targetType: "LISTING",
          listingId: target.listingId,
          reason: values.reason as never,
          details: values.details,
        });
      } else if (target.type === "BROKER") {
        await createMutation.mutateAsync({
          targetType: "BROKER",
          userId: target.userId,
          reason: values.reason as never,
          details: values.details,
        });
      } else {
        await createMutation.mutateAsync({
          targetType: "CONVERSATION",
          conversationId: target.conversationId,
          reason: values.reason as never,
          details: values.details,
        });
      }

      toast.success(t("saas.report.dialog.success"));
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      displayErrorToast(err);
      form.setError("root", { message: formatError(err) });
    }
  };

  const reasonField = (
    <FormField
      control={form.control}
      name="reason"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t("saas.report.dialog.reasonLabel")}</FormLabel>
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              value={field.value}
              className="flex flex-col gap-3"
            >
              {reasons.map((reason) => (
                <div key={reason} className="flex items-center gap-2">
                  <RadioGroupItem value={reason} id={`reason-${reason}`} />
                  <Label
                    htmlFor={`reason-${reason}`}
                    className="cursor-pointer font-normal text-sm leading-snug"
                  >
                    {t(`saas.report.reasons.${reason}` as never)}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const detailsField = (
    <FormField
      control={form.control}
      name="details"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t("saas.report.dialog.detailsLabel")}</FormLabel>
          <FormControl>
            <Textarea
              {...field}
              placeholder={t("saas.report.dialog.detailsPlaceholder")}
              rows={4}
              className="resize-none"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const rootError =
    form.formState.errors.root?.message != null ? (
      <p className="text-sm text-destructive">
        {form.formState.errors.root.message}
      </p>
    ) : null;

  const formInner = (
    <form
      id="report-dialog-form"
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      {reasonField}
      {detailsField}
      {rootError}

      {isDesktop ? (
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {t("saas.report.dialog.submit")}
          </Button>
        </DialogFooter>
      ) : null}
    </form>
  );

  return <Form {...form}>{formInner}</Form>;
}

export function ReportDialog({
  open,
  onOpenChange,
  target,
}: ReportDialogProps) {
  const t = useTranslations();
  const isDesktop = useMediaQuery(mediaQuery.sm);

  const { data: reasonOptions, isLoading: reasonsLoading } =
    api.reports.getReasonOptions.useQuery(
      { targetType: target.type },
      { enabled: open },
    );

  const reasons = reasonOptions?.reasons ?? [];

  const titleKey =
    target.type === "LISTING"
      ? "saas.report.dialog.title.listing"
      : target.type === "BROKER"
        ? "saas.report.dialog.title.broker"
        : "saas.report.dialog.title.conversation";

  const body = reasonsLoading ? (
    <p className="text-sm text-muted-foreground py-2">{t("common.loading")}</p>
  ) : reasons.length === 0 ? (
    <p className="text-sm text-muted-foreground py-2">
      {t("saas.report.dialog.loadError")}
    </p>
  ) : (
    <ReportFormInner
      key={reasons.join(",")}
      reasons={[...reasons]}
      target={target}
      isDesktop={isDesktop}
      onOpenChange={onOpenChange}
    />
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t(titleKey as never)}</DialogTitle>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{t(titleKey as never)}</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-2 overflow-y-auto max-h-[70vh]">{body}</div>
        <DrawerFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="report-dialog-form"
            disabled={reasonsLoading || reasons.length === 0}
          >
            {t("saas.report.dialog.submit")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
